import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './FeedbackAdmin.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FeedbackAdmin = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, implemented
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  useEffect(() => {
    fetchFeedbacks();
  }, [filter]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const filterQuery = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/feedback/list${filterQuery}`);
      setFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId, newStatus, adminNotes = null) => {
    try {
      await axios.patch(`${API}/feedback/${feedbackId}/status`, null, {
        params: { status: newStatus, admin_notes: adminNotes }
      });
      fetchFeedbacks(); // Refresh list
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Error updating status');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'reviewed': return 'status-reviewed';
      case 'implemented': return 'status-implemented';
      default: return 'status-pending';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="feedback-admin-container">
      <div className="feedback-admin-header">
        <h1>📝 Feedback Administration</h1>
        <button onClick={() => navigate('/')} className="btn-back">
          ← Zurück
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="feedback-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Alle ({feedbacks.length})
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Ausstehend
        </button>
        <button
          className={`filter-btn ${filter === 'reviewed' ? 'active' : ''}`}
          onClick={() => setFilter('reviewed')}
        >
          Überprüft
        </button>
        <button
          className={`filter-btn ${filter === 'implemented' ? 'active' : ''}`}
          onClick={() => setFilter('implemented')}
        >
          Implementiert
        </button>
      </div>

      {/* Feedbacks List */}
      {loading ? (
        <div className="feedback-loading">Lade Feedbacks...</div>
      ) : feedbacks.length === 0 ? (
        <div className="feedback-empty">Keine Feedbacks gefunden.</div>
      ) : (
        <div className="feedbacks-list">
          {feedbacks.map((feedback) => (
            <div key={feedback.id} className="feedback-card">
              <div className="feedback-card-header">
                <div className="feedback-meta">
                  <span className={`status-badge ${getStatusBadgeClass(feedback.status)}`}>
                    {feedback.status}
                  </span>
                  <span className="feedback-date">{formatDate(feedback.created_at)}</span>
                </div>
                <button
                  className="btn-expand"
                  onClick={() => setExpandedFeedback(
                    expandedFeedback === feedback.id ? null : feedback.id
                  )}
                >
                  {expandedFeedback === feedback.id ? '▼ Details ausblenden' : '▶ Details anzeigen'}
                </button>
              </div>

              <div className="feedback-preview">
                <div className="preview-section">
                  <strong>Beschwerde:</strong>
                  <p>{feedback.complaint}</p>
                </div>
                <div className="preview-section">
                  <strong>Vorschlag:</strong>
                  <p>{feedback.suggestion}</p>
                </div>
              </div>

              {expandedFeedback === feedback.id && (
                <div className="feedback-details">
                  <div className="detail-section">
                    <h4>Kontext</h4>
                    <div className="context-box">
                      <p><strong>Thesis:</strong> {feedback.debate_context?.thesis}</p>
                      <p><strong>Sprache:</strong> {feedback.debate_context?.language}</p>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Argument (User)</h4>
                    <div className="context-box">
                      {feedback.argument_text}
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>AI-Bewertung</h4>
                    <div className="context-box">
                      <p><strong>Punkte:</strong> {feedback.ai_rating?.score?.total_score || 0}</p>
                      <p><strong>Valid:</strong> {feedback.ai_rating?.is_valid ? 'Ja' : 'Nein'}</p>
                      <p><strong>Tags:</strong> {feedback.ai_rating?.score?.tags?.join(', ') || 'Keine'}</p>
                      {feedback.ai_rating?.ai_feedback && (
                        <p><strong>AI Feedback:</strong> {feedback.ai_rating.ai_feedback}</p>
                      )}
                    </div>
                  </div>

                  {feedback.additional_comment && (
                    <div className="detail-section">
                      <h4>Zusätzlicher Kommentar</h4>
                      <div className="context-box">
                        {feedback.additional_comment}
                      </div>
                    </div>
                  )}

                  {feedback.admin_notes && (
                    <div className="detail-section">
                      <h4>Admin-Notizen</h4>
                      <div className="context-box admin-notes">
                        {feedback.admin_notes}
                      </div>
                    </div>
                  )}

                  {/* Status Actions */}
                  <div className="status-actions">
                    <h4>Status ändern:</h4>
                    <div className="action-buttons">
                      {feedback.status !== 'pending' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'pending')}
                          className="btn-status btn-pending"
                        >
                          → Ausstehend
                        </button>
                      )}
                      {feedback.status !== 'reviewed' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'reviewed')}
                          className="btn-status btn-reviewed"
                        >
                          → Überprüft
                        </button>
                      )}
                      {feedback.status !== 'implemented' && (
                        <button
                          onClick={() => updateStatus(feedback.id, 'implemented')}
                          className="btn-status btn-implemented"
                        >
                          → Implementiert
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackAdmin;
